import React, { useEffect, useState, Fragment, useContext } from 'react'
import PropTypes from 'prop-types'
import display from 'lib/Display'
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import NavigationButtons from 'components/bookings/NavigationButtons'
import CheckoutContext from 'components/bookings/contexts/CheckoutContext'

const PaymentForm = ({
  order,
  user,
  leftToPayWithStripe,
  setErrors,
  urls,
  authenticityToken
}) => {
  const stripe = useStripe()
  const elements = useElements()
  const context = useContext(CheckoutContext)

  const [disableSubmitButton, setDisableSubmitButton] = useState(true)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (stripe && elements) setDisableSubmitButton(false)
  }, [stripe, elements])

  const confirmPayment = async () => {
    if (!stripe || !elements) {
      return
    }

    setIsLoading(true)
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: urls.returnURL
      }
    })

    if (error) {
      fetch(urls.paymentErrorURL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          authenticity_token: authenticityToken,
          payable_id: order.id,
          payable_type: 'Order'
        })
      })
        .then(async (response) => {
          if (response.status === 404) {
            const errorData = await response.json().catch(() => ({}))
            console.error('Order not found:', errorData)
            return null
          }
          if (!response.ok) {
            throw new Error(`${response.status}`)
          }
          if (response.status === 204) {
            return null
          }
        })
        .catch((error) => {
          console.error('Network or server error:', error)
        })

      if (
        error.type === 'card_error' ||
        error.type === 'invalid_request_error'
      ) {
        setErrors([{ field: 'Erreur', message: error.message }])
      } else {
        setErrors([
          {
            field: 'Erreur',
            message:
              'Une erreur est survenue lors du paiement, veuillez réessayer'
          }
        ])
      }
    }
    setIsLoading(false)
  }

  const buttonLabel = `Payer ${display.currency(leftToPayWithStripe)}`
  const paymentElementOptions = {
    layout: {
      type: 'accordion',
      defaultCollapsed: false,
      radios: true,
      spacedAccordionItems: false
    },
    defaultValues: {
      billingDetails: {
        name: `${user.fname} ${user.lname}`,
        email: user.email,
        phone: user.phone
      }
    },
    business: { name: 'Babasport' }
  }

  return (
    <Fragment>
      <PaymentElement id="payment-element" options={paymentElementOptions} />
      <div className="row mt-2">
        <div className="col-sm-12">
          <div className="notice">
            En validant ma commande, je déclare avoir pris connaissance et
            accepté les{' '}
            <a href={urls.aboutTermsURL} target="_blank" rel="noreferrer">
              conditions générales de vente
            </a>
            .
          </div>
        </div>
      </div>
      <NavigationButtons
        priceAfter={order.priceAfter}
        goPrevious={context.goPrevious}
        submitLabel={buttonLabel}
        handleValidate={confirmPayment}
        disableSubmitButton={isLoading || disableSubmitButton}
      />
    </Fragment>
  )
}

PaymentForm.propTypes = {
  order: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  leftToPayWithStripe: PropTypes.string.isRequired,
  setErrors: PropTypes.func.isRequired,
  urls: PropTypes.object.isRequired,
  authenticityToken: PropTypes.string.isRequired
}

export default PaymentForm
